<template>
  <div class="calculus-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>{{ mode === 'diff' ? "f'(x) = dy/dx" : "∫ f(x)dx = F(x)" }}$</div>
    </div>

    <div class="main-layout alternate">
      <div class="canvas-area">
        <svg width="100%" height="320" viewBox="0 0 500 320" ref="svgStage">
          <!-- Coordinates -->
          <line x1="50" y1="280" x2="450" y2="280" stroke="#444" stroke-width="1" marker-end="url(#arrow)" />
          <line x1="50" y1="50" x2="50" y2="290" stroke="#444" stroke-width="1" marker-end="url(#arrow)" />
          
          <!-- Integration Rectangles (Riemann Sum) -->
          <g v-if="mode === 'int'">
            <rect v-for="(rect, i) in riemannRects" :key="i"
              :x="rect.x" :y="rect.y" :width="rect.width" :height="rect.height"
              fill="rgba(0, 210, 255, 0.3)" stroke="rgba(0, 210, 255, 0.5)" />
            <path :d="curvePath" fill="none" stroke="#fff" stroke-width="2" />
          </g>

          <!-- Differentiation Tangent -->
          <g v-if="mode === 'diff'">
            <path :d="curvePath" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="2" />
            <line :x1="tangent.x1" :y1="tangent.y1" :x2="tangent.x2" :y2="tangent.y2" 
              stroke="#ff4757" stroke-width="2" />
            <circle :cx="pointerX" :cy="getCurveY(pointerX)" r="4" fill="#ff4757" />
          </g>
          
          <defs>
            <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
              <path d="M0,0 L8,3 L0,6 Z" fill="#444" />
            </marker>
          </defs>
        </svg>

        <div class="hud-box glass">
          <div v-if="mode === 'int'" class="stat-item">
            <label>矩形数量 (n):</label> <span>{{ rectCount }}</span>
            <p>越密集的矩形越接近真实面积</p>
          </div>
          <div v-if="mode === 'diff'" class="stat-item">
            <label>当前斜率 k:</label> <span>{{ tangentSlope.toFixed(2) }}</span>
            <p>斜率代表了函数在该点的瞬时变化率</p>
          </div>
        </div>
      </div>

      <div class="control-panel glass-pro">
        <div class="mode-switch">
          <div :class="['mode-btn', {active: mode === 'int'}]" @click="mode = 'int'">
            <i class="el-icon-finished"></i> 定积分 (面积)
          </div>
          <div :class="['mode-btn', {active: mode === 'diff'}]" @click="mode = 'diff'">
            <i class="el-icon-top-right"></i> 微分 (斜率)
          </div>
        </div>

        <div v-if="mode === 'int'" class="control-group">
          <label>分割精细度 (dx)</label>
          <el-slider v-model="rectCount" :min="4" :max="100" size="mini"></el-slider>
        </div>

        <div v-if="mode === 'diff'" class="control-group">
          <label>探测点位置 (x)</label>
          <el-slider v-model="pointerX" :min="60" :max="440" size="mini"></el-slider>
        </div>

        <div class="theory-text">
          <p v-if="mode === 'int'"><strong>积分思想：</strong>将复杂的形状切割成无数个微小的规则矩形（微分），再将它们的面积进行累加（合），这就是“积”分。</p>
          <p v-else><strong>微分思想：</strong>观察极短时间内函数值的变化。当时间间隔趋于无限小时，这条割线就变成了切线，斜率就是导数。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CalculusViz',
  data() {
    return {
      mode: 'int',
      rectCount: 12,
      pointerX: 200,
      startX: 60,
      endX: 440,
      baselineY: 280
    }
  },
  computed: {
    curvePath() {
      let d = "";
      for (let x = this.startX; x <= this.endX; x++) {
        const y = this.getCurveY(x);
        d += (x === this.startX ? "M" : "L") + ` ${x} ${y}`;
      }
      return d;
    },
    riemannRects() {
      const rects = [];
      const step = (this.endX - this.startX) / this.rectCount;
      for (let i = 0; i < this.rectCount; i++) {
        const x = this.startX + i * step;
        const y = this.getCurveY(x);
        rects.push({
          x: x,
          y: y,
          width: step,
          height: this.baselineY - y
        });
      }
      return rects;
    },
    tangent() {
      const x = this.pointerX;
      const y = this.getCurveY(x);
      const slope = this.getSlope(x);
      const len = 60;
      return {
        x1: x - len, y1: y - slope * len,
        x2: x + len, y2: y + slope * len
      };
    },
    tangentSlope() {
      return -this.getSlope(this.pointerX); // Invert because SVG Y increases downwards
    }
  },
  methods: {
    getCurveY(x) {
      // Sine wave based curve for visualization
      const normalizedX = (x - 250) / 100;
      return 150 + Math.sin(normalizedX) * 60 + Math.pow(normalizedX, 2) * 20;
    },
    getSlope(x) {
      const h = 0.1;
      return (this.getCurveY(x + h) - this.getCurveY(x)) / h;
    }
  }
}
</script>

<style scoped lang="scss">
.calculus-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; color: #fff; }
.viz-header { display: flex; justify-content: space-between; padding: 10px; background: rgba(255,255,255,0.03); border-radius: 8px; }

.main-layout { display: flex; flex: 1; gap: 15px; min-height: 0; }
.canvas-area { 
  flex: 1.5; background: #010409; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05);
  display: flex; flex-direction: column; justify-content: center;
  .hud-box { position: absolute; top: 15px; right: 15px; padding: 12px; border-radius: 8px; border-right: 3px solid #00d2ff; width: 160px; }
  .stat-item { label { font-size: 0.7rem; color: #888; } span { font-family: monospace; font-size: 1.1rem; color: #00d2ff; } p { font-size: 0.6rem; color: #444; margin: 4px 0 0; } }
}

.control-panel { 
  width: 280px; padding: 20px; display: flex; flex-direction: column; gap: 20px;
  .mode-switch {
    display: flex; background: rgba(0,0,0,0.3); border-radius: 8px; padding: 4px;
    .mode-btn { 
      flex: 1; text-align: center; padding: 8px 0; font-size: 0.75rem; cursor: pointer; border-radius: 6px; transition: all 0.3s; color: #666;
      &.active { background: #00d2ff; color: #fff; box-shadow: 0 4px 12px rgba(0, 210, 255, 0.3); }
    }
  }
  .control-group { label { font-size: 0.75rem; color: #888; margin-bottom: 8px; display: block; } }
  .theory-text { background: rgba(255, 255, 255, 0.02); padding: 12px; border-radius: 8px; border-left: 2px solid #00d2ff; p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } } }
}
</style>
